import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TrashIcon } from "lucide-react";
import { NodeApi } from "~/api/node-api";
import { ComboBox } from "~/components/common/combo-box";
import { Button } from "~/components/ui";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import type { ReportTag } from "~/types/api";
import type { FormOption } from "~/types/ui";

export default function ReportTags({
  value,
  onChange,
  options,
}: {
  value: ReportTag[];
  onChange: (val: ReportTag[]) => void;
  options: { label: string; value: any[] }[];
}) {
  const { t } = useTranslation();
  const nodeOptions =
    (options?.find((x) => x.label == "nodes")?.value as FormOption<string>[]) ??
    [];

  const [deveui, setDeveui] = useState<string | undefined>(undefined);
  const [key, setKey] = useState<string | undefined>(undefined);

  const { data: keyOptions = [] } = useQuery({
    queryKey: ["reportTags:keys", deveui],
    enabled: !!deveui,
    queryFn: async () => {
      const nodeApi = new NodeApi();
      const res = await nodeApi.getKeysForTag(deveui as string);
      if (!res.ok) return [];
      return (
        res.data?.map(
          (k) =>
            ({
              label: k.label || k.key,
              value: k.key,
            }) as FormOption<string>,
        ) ?? []
      );
    },
  });

  function addTag() {
    if (!deveui || !key) return;
    if (value.some((v) => v.deveui === deveui && v.key === key)) return;

    onChange([...value, { deveui, key }]);
    setDeveui(undefined);
    setKey(undefined);
  }

  function removeTag(i: number) {
    onChange(value.filter((_, idx) => idx !== i));
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-2 items-center">
        <div className="w-full">
          <ComboBox
            options={nodeOptions}
            value={deveui}
            onChange={(v) => {
              setDeveui(v as string);
              setKey(undefined);
            }}
            placeholder={t("reports.placeholders.tag")}
          />
        </div>
        <div className="w-full">
          <ComboBox
            options={keyOptions}
            value={key}
            onChange={(v) => setKey(v as string)}
            disabled={!deveui}
            placeholder={t("reports.placeholders.tagDataKey")}
          />
        </div>
        <Button type="button" variant="outline" onClick={addTag}>
          {t("common.add")}
        </Button>
      </div>

      {value.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("reports.fields.tag")}</TableHead>
              <TableHead>{t("reports.fields.key")}</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {value.map((v, i) => (
              <TableRow key={i}>
                <TableCell>{v.deveui}</TableCell>
                <TableCell>{v.key}</TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="icon"
                    type="button"
                    onClick={() => removeTag(i)}
                  >
                    <TrashIcon className="h-4 w-4" color="red" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
